import React from 'react'
import { documentToReactComponents } from '@contentful/rich-text-react-renderer'
import { Marquee } from '@devnomic/marquee'
import classNames from 'classnames'
import LazyHydrate from 'react-lazy-hydration'
import { LinkButton } from '@/atoms/Button'
import { GuildHighResShieldIcon } from '@/atoms/Icons/GuildHighResShieldIcon'
import { ResponsiveBackgroundImage } from '@/atoms/ResponsiveBackgroundImage'
import { AsH1, AsLabel, AsSpan, HeadingXS, LabelLG, ParagraphSM } from '@/atoms/Text'
import { BoundedImage } from '@/molecules/BoundedImage'
import { options } from '@/services/ContentfulService'
import { isGuildSignupScrollingLayout } from '@/services/GuildSignupPageService/validations'
import { Translate, useTranslate } from '@/utils/translate/translate-client'
import useWindowSize from '@/utils/useWindowSize'
import { useGuildSignupContext } from '@/views/GuildSignupView/GuildSignupContext'

export const MarketingWinbackHero: React.FC = () => {
  const { layout, sale, slug } = useGuildSignupContext()
  const { t } = useTranslate('guild')

  if (!isGuildSignupScrollingLayout(layout)) {
    return null
  }

  const scrollingLayout = layout

  const isDryBar = slug?.indexOf('drybar') !== -1 || slug?.indexOf('dbcplus') !== -1
  const isTMobile = slug?.indexOf('tmtdec25') !== -1

  const spritesheet = isDryBar
    ? 'https://images.angelstudios.com/image/upload/v1745438972/angel-studios/guild/dry_bar_css_sprites-2.webp'
    : 'https://images.angelstudios.com/image/upload/v1732132444/angel-studios/guild/hero/css_sprites_5.webp'

  return (
    <section
      aria-label={scrollingLayout?.heading ?? undefined}
      className="2xl:max-h-auto relative h-full min-h-screen w-full overflow-hidden"
    >
      <link rel="preload" href={spritesheet} as="image"></link>
      {scrollingLayout?.backgroundHeroImage && (
        <ResponsiveBackgroundImage
          src={scrollingLayout.backgroundHeroImage}
          className="pointer-events-none touch-none select-none"
          transforms="q_auto:low"
        />
      )}
      <div
        className={classNames(
          'h-screen min-h-screen absolute left-0 top-0 z-10 w-full bg-transparent px-4 md:max-h-[800px] md:px-8 xl:mx-[40px] xl:px-4',
          { 'mt-16 md:mt-0': !!sale },
        )}
      >
        <div className="relative flex h-full w-full flex-col-reverse items-center justify-center lg:!flex-row 2xl:mx-auto 2xl:max-w-[1600px]">
          <div className="-mt-auto flex h-full w-full flex-col items-start justify-center gap-y-4 bg-transparent py-0 opacity-100 md:items-center md:gap-y-6 lg:-mt-40 lg:w-1/2 lg:items-start lg:py-32 2xl:pl-10">
            <div className="flex flex-row">
              <div className="flex flex-col items-center gap-y-1">
                <GuildHighResShieldIcon className="invisible h-0 w-0 md:visible md:h-full md:w-fit" size={64} />
                <GuildHighResShieldIcon
                  className="invisible h-0 w-0 sm:visible sm:h-full sm:w-fit md:invisible md:h-0 md:w-0"
                  size={48}
                />
                <ParagraphSM color="white" className="hidden uppercase md:flex">
                  {t('guild', 'Guild')}
                </ParagraphSM>
              </div>
              {isTMobile && (
                <div className="hidden md:flex">
                  <BoundedImage
                    className="mb-2 flex lg:rounded-xl"
                    alt={t('tMobileLogo', 'T-mobile logo')}
                    path={
                      'https://images.angelstudios.com/image/upload/v1765302394/angel-studios/misc-icons/TMT-logo.png'
                    }
                    width={600}
                    height={104}
                    sizes="(max-width: 640px) 358px, (max-width: 1024px) 600px, 600px"
                    imageClassName="h-auto max-h-[40px] w-auto max-w-full object-contain md:max-h-[80px] lg:max-h-[104px] lg:max-w-[600px]"
                  />
                </div>
              )}
            </div>
            <div className="z-20 -mt-[80px] flex max-w-[600px] flex-col gap-y-4 md:mt-0 se:max-w-full ">
              {isTMobile && (
                <div className="-mt-20 flex w-full items-center justify-center md:m-0 md:hidden">
                  <BoundedImage
                    className="mb-2 flex rounded-xl"
                    alt={t('tMobileLogo', 'T-mobile logo')}
                    path={
                      'https://images.angelstudios.com/image/upload/v1765302394/angel-studios/misc-icons/TMT-logo.png'
                    }
                    width={600}
                    height={104}
                    sizes="(max-width: 640px) 358px, (max-width: 1024px) 600px, 600px"
                    imageClassName="h-auto max-h-[40px] w-auto max-w-full object-contain md:max-h-[80px] lg:max-h-[104px] lg:max-w-[600px]"
                  />
                </div>
              )}
              <HeadingXS
                as={AsH1}
                weight="bold"
                className="se:photon-title-lg md:photon-heading-lg 2xl:photon-heading-xl w-full text-balance bg-transparent text-center md:text-left"
                color="white"
              >
                {scrollingLayout.heading}
              </HeadingXS>
              <ParagraphSM
                as={AsSpan}
                className="[&>p]:photon-paragraph-sm md:[&>p]:photon-paragraph-md se:[&>p]:photon-caption-sm 2xl:[&>p]:photon-paragraph-xl mb-3 w-full text-balance bg-transparent text-center md:text-left"
                color="core-gray-400"
              >
                {documentToReactComponents(scrollingLayout?.paragraph?.json, options)}
              </ParagraphSM>
            </div>
            <div className="flex w-full max-w-[600px] flex-col !opacity-100">
              <LinkButton
                href="#plan-selection"
                variant="oxide"
                className={'btn my-0 mb-4 min-h-[40px] min-w-[142px] rounded-lg py-2 lg:min-h-[56px] lg:w-2/5 lg:px-0'}
              >
                <LabelLG
                  as={AsLabel}
                  color="white"
                  weight="semibold"
                  className="se:photon-label-md flex h-full cursor-pointer flex-row items-center justify-center gap-1"
                >
                  <Translate i18nKey="getStarted" t={t}>
                    Get Started
                  </Translate>
                </LabelLG>
              </LinkButton>
            </div>
          </div>
          <ScrollingTitles spritesheet={spritesheet} />
        </div>
      </div>
      <div
        className="absolute inset-0 h-full w-[130%]"
        style={{
          background: `linear-gradient(180deg, ${isDryBar ? '#000068' : 'rgba(20, 20, 20, 0.00)'} 0%, #222222 100%)`,
        }}
      />
    </section>
  )
}

export function ScrollingTitles(props: { className?: string; spritesheet: string }) {
  const { innerWidth, innerHeight } = useWindowSize()
  const imageHeight = React.useMemo(() => {
    return innerWidth > 2000
      ? '400px'
      : innerWidth > 1300
      ? '300px'
      : innerWidth > 768
      ? '220px'
      : innerHeight > 670
      ? '165px'
      : '140px'
  }, [innerWidth, innerHeight])
  const imageWidth = React.useMemo(() => {
    return innerWidth > 2000
      ? '267px'
      : innerWidth > 1300
      ? '200px'
      : innerWidth > 768
      ? '147px'
      : innerHeight > 670
      ? '110px'
      : '93px'
  }, [innerWidth, innerHeight])
  const imageZoom = React.useMemo(() => {
    return innerWidth > 2000 ? 1.33 : innerWidth > 1300 ? 0 : innerWidth > 768 ? 0.73 : innerHeight > 670 ? 0.55 : 0.46
  }, [innerWidth, innerHeight])

  const CABRINI = { x: -10, y: -10 }
  const SOUND_OF_HOPE_THE_STORY_OF_POSSUM_TROT = { x: -230, y: -10 }
  const THE_LUNATIC_FARMER = { x: -450, y: -10 }
  const SOUND_OF_FREEDOM = { x: -670, y: -10 }
  const HOMESTEAD = { x: -890, y: -10 }
  const THE_MOSAIC_CHURCH = { x: -10, y: -330 }
  const BONHOEFFER = { x: -230, y: -330 }
  const SOMETHING_TO_STAND_FOR_WITH_MIKE_ROWE = { x: -450, y: -330 }
  const THE_SHIFT = { x: -670, y: -330 }
  const THE_KING_OF_KINGS = { x: -890, y: -330 }
  const TUTTLE_TWINS = { x: -10, y: -650 }
  const WINGFEATHER_SAGA = { x: -230, y: -650 }
  const THE_LAST_RIFLEMAN = { x: -450, y: -650 }
  const RIOT = { x: -670, y: -650 }
  const GABRIEL_AND_THE_GUARDIANS = { x: -890, y: -650 }

  const { spritesheet } = props

  const firstColumn = [
    CABRINI,
    SOUND_OF_HOPE_THE_STORY_OF_POSSUM_TROT,
    TUTTLE_TWINS,
    THE_LAST_RIFLEMAN,
    THE_MOSAIC_CHURCH,
  ]
  const secondColumn = [
    THE_SHIFT,
    THE_KING_OF_KINGS,
    HOMESTEAD,
    SOMETHING_TO_STAND_FOR_WITH_MIKE_ROWE,
    SOUND_OF_FREEDOM,
  ]
  const thirdColumn = [BONHOEFFER, WINGFEATHER_SAGA, THE_LUNATIC_FARMER, RIOT, GABRIEL_AND_THE_GUARDIANS]

  return (
    <div
      className={classNames(
        'z-10 mb-[45px] flex h-1/3 w-full justify-center gap-x-4 opacity-70 lg:my-0 lg:h-full lg:w-1/2 lg:justify-center lg:opacity-100',
        props.className,
      )}
    >
      <LazyHydrate whenVisible>
        <>
          {innerWidth && innerHeight && (
            <Marquee direction="up" reverse className="fade-marquee h-full w-1/3 animate-fadein md:w-auto">
              {firstColumn?.map((poster) => {
                const background = `url('${spritesheet}') ${poster.x}px ${poster.y}px`
                return (
                  <div
                    key={JSON.stringify({ poster })}
                    style={{
                      height: imageHeight,
                      width: imageWidth,
                    }}
                    className="rounded-2xl"
                  >
                    <div
                      style={{
                        background,
                        zoom: imageZoom,
                      }}
                      className="pointer-events-none h-full w-full max-w-full touch-none select-none rounded-2xl bg-center"
                    />
                  </div>
                )
              })}
            </Marquee>
          )}
        </>
      </LazyHydrate>
      <LazyHydrate whenVisible>
        <>
          {innerWidth && innerHeight && (
            <Marquee
              direction="up"
              className="fade-marquee h-full w-1/3 animate-fadein [--duration:32s] md:w-auto"
              innerClassName="[--duration:32s]"
            >
              {secondColumn?.map((poster) => {
                const background = `url('${spritesheet}') ${poster.x}px ${poster.y}px`
                return (
                  <div
                    key={JSON.stringify({ poster })}
                    style={{
                      height: imageHeight,
                      width: imageWidth,
                    }}
                    className="rounded-2xl"
                  >
                    <div
                      style={{
                        background,
                        zoom: imageZoom,
                      }}
                      className="pointer-events-none h-full w-full max-w-full touch-none select-none rounded-2xl bg-center "
                    />
                  </div>
                )
              })}
            </Marquee>
          )}
        </>
      </LazyHydrate>
      <LazyHydrate whenVisible>
        <>
          {innerWidth && innerHeight && (
            <Marquee direction="up" reverse className="fade-marquee w-1/3 animate-fadein md:w-auto">
              {thirdColumn?.map((poster) => {
                const background = `url('${spritesheet}') ${poster.x}px ${poster.y}px`
                return (
                  <div
                    key={JSON.stringify({ poster })}
                    style={{
                      height: imageHeight,
                      width: imageWidth,
                    }}
                    className="rounded-2xl"
                  >
                    <div
                      style={{
                        background,
                        zoom: imageZoom,
                      }}
                      className="pointer-events-none h-full w-full max-w-full touch-none select-none rounded-2xl bg-center"
                    />
                  </div>
                )
              })}
            </Marquee>
          )}
        </>
      </LazyHydrate>
    </div>
  )
}
